// =====================================================================================
// INVRT Radar - web console - the campaign manual upload tab (story O05)
//
// ONE JOB PER SCREEN (Mark's ruling): a manual CSV upload for this one campaign, through
// the engine's existing trusted-source machinery. The surface is honest about every
// state, and the truths it must never bend are:
//
//   - The engine registers the file exactly once (streaming sha256), validates it
//     technically, and - because the client sends run=true, exactly as the Drive pull
//     path does - imports it in the same request. While the POST is in flight the only
//     honest progress is "In progress"; there is no percentage, because there is no
//     progress stream to read one from.
//   - The disposition counts are the engine's OWN response counters (parsed records, new
//     leads, per-reason refusals). Nothing here is computed, inferred or invented.
//   - An uploaded file is NEVER auto-attested US. Attestation is a separate trusted-source
//     operator step, and this surface says so beside the batch's real attestation state
//     from the org-delivery readiness read.
//   - A duplicate names the existing batch: the engine's 409 body is the truth.
//   - A cancelled upload never claims "cancelled-imported". The engine holds no cleanup
//     for an aborted request, so the surface says the engine may still finish in the
//     background and points at the source list, which shows what actually registered.
//   - NO raw PII: the file's contents are never rendered. Its name, size and the counts.
//
// The engine exposes no per-batch job-status GET on the console surfaces, so the
// post-registration poll is the existing signed readiness read (the campaign's source
// list): it is refetched while this session's batch is still unattested. A failed
// readiness read never blocks the upload form; it fails on its own with a retry (U01).
// =====================================================================================

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { ApiError, isApiError } from "@/lib/api";
import {
  fetchDeliveryReadiness,
  hiDeliveryConfigError,
  hiDeliveryOrigin,
  type HiDeliveryContext,
} from "@/lib/hiDelivery";
import {
  INGEST_MAX_BODY_BYTES,
  uploadBatch,
  type IngestBatchResult,
} from "@/lib/hiIngest";
import { useSession } from "@/auth/SessionProvider";

// ---------------------------------------------------------------------------------------
// The job state machine (one job per screen)
// ---------------------------------------------------------------------------------------

type JobState =
  | { kind: "idle" }
  | { kind: "in_flight"; fileName: string; sizeLabel: string }
  | { kind: "registered"; result: IngestBatchResult }
  | { kind: "cancelled"; fileName: string }
  | { kind: "failed"; error: ApiError; fileName: string };

/** Human bytes for the file preview. Exact under 1 KB, one decimal above. */
function formatBytes(size: number): string {
  if (size < 1024) return `${size} bytes`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** The engine's counter keys in the operator's words; the pairing the counters response
 * actually carries (batch_manifest's own columns), never a UI-derived total. */
const COUNTER_LABELS: Array<{ key: keyof NonNullable<IngestBatchResult["counters"]>; label: string }> = [
  { key: "parsedRecords", label: "Records parsed" },
  { key: "newLeads", label: "New leads accepted" },
  { key: "rejectedQuality", label: "Rejected by import-time quality rules" },
  { key: "reIntentCounted", label: "Re-intent events counted" },
  { key: "reDeliveriesNotCounted", label: "Re-deliveries seen (never counted)" },
  { key: "invalid", label: "Invalid rows" },
  { key: "quarantined", label: "Rows quarantined" },
];

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

// ---------------------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------------------

export function CampaignImportTab({ campaignKey }: { campaignKey: string }) {
  const { session, orgId } = useSession();
  const cache = useQueryClient();
  const userId = session?.user.id ?? null;
  const accessToken = session?.access_token ?? null;

  const origin = hiDeliveryOrigin();
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<JobState>({ kind: "idle" });
  /** This session's newest registered batch; the readiness poll follows its attestation. */
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const jobIdRef = useRef(0);
  const statusRef = useRef<HTMLParagraphElement>(null);

  const ctx: HiDeliveryContext = { origin: origin ?? "", accessToken };

  // The readiness read IS the post-registration poll target: the engine holds no
  // per-batch job-status GET on the console surfaces, so the campaign's source list
  // (each batch's attestation state) is what this surface polls and renders.
  const readiness = useQuery({
    queryKey: ["hi-import", "readiness", userId, orgId, campaignKey],
    enabled: origin !== null && orgId !== null && userId !== null && campaignKey !== "",
    queryFn: async ({ signal }) => {
      if (origin === null) throw hiDeliveryConfigError();
      return fetchDeliveryReadiness({ ...ctx, signal }, campaignKey, orgId ?? "");
    },
    staleTime: 15_000,
    // Poll while this session's batch sits unattested in the list; stop once it is
    // attested, revoked, or there is no batch of ours to watch.
    refetchInterval: (query) => {
      if (activeBatchId === null) return false;
      const row = query.state.data?.sources.find((source) => source.batchId === activeBatchId);
      return row === undefined || (!row.attested && !row.revoked) ? 15_000 : false;
    },
  });

  const upload = useMutation({
    mutationFn: async (target: File): Promise<{ id: number; result: IngestBatchResult | null }> => {
      const id = ++jobIdRef.current;
      const controller = new AbortController();
      abortRef.current = controller;
      setJob({ kind: "in_flight", fileName: target.name, sizeLabel: formatBytes(target.size) });
      try {
        return { id, result: await uploadBatch({ ...ctx, signal: controller.signal }, campaignKey, target) };
      } catch (error) {
        if (isAbort(error)) return { id, result: null }; // a cancel already owns the screen
        throw error;
      }
    },
    onSuccess: ({ result, id }) => {
      if (id !== jobIdRef.current || result === null) return; // a cancel (or a newer job) owns the screen
      setJob({ kind: "registered", result });
      setActiveBatchId(result.batchId);
      void cache.invalidateQueries({ queryKey: ["hi-import", "readiness", userId, orgId, campaignKey] });
    },
    onError: (error, target) => {
      if (isAbort(error) && abortRef.current !== null) {
        // Cancelled from the Cancel control: the truth is below, never "cancelled-imported".
        setJob({ kind: "cancelled", fileName: target.name });
        return;
      }
      setJob({
        kind: "failed",
        error: isApiError(error)
          ? error
          : new ApiError({ token: "internal_error", message: "The upload failed.", httpStatus: 0, correlationId: null }),
        fileName: target.name,
      });
    },
  });

  // Focus lands on the outcome line once a job settles, so keyboard users read the
  // outcome where their focus went (the delivery tab's law).
  useEffect(() => {
    if ((job.kind === "registered" || job.kind === "cancelled" || job.kind === "failed") && statusRef.current) {
      statusRef.current.focus();
    }
  }, [job]);

  if (origin === null) {
    return (
      <EmptyState
        tone="error"
        title="Import is not connected in this build."
        body="The console build serving this page was not given the HIGH-INTENT ingest endpoint, so nothing can be uploaded. This is a deployment setting, not something to fix at your end. Nothing was uploaded or changed."
      />
    );
  }

  const pickFile = (chosen: File | null) => {
    // A new choice starts a NEW job: the previous outcome makes way, one job per screen.
    setFile(chosen);
    setJob({ kind: "idle" });
  };

  function startUpload() {
    if (file === null || upload.isPending) return;
    // The engine's own 50 MB law, mirrored as a pre-flight: refuse before a byte moves.
    if (file.size > INGEST_MAX_BODY_BYTES) {
      setJob({
        kind: "failed",
        error: new ApiError({
          token: "upload_too_large",
          message: "That file is larger than the engine's 50 MB limit. Nothing was uploaded.",
          httpStatus: 413,
          correlationId: null,
        }),
        fileName: file.name,
      });
      return;
    }
    upload.mutate(file);
  }

  function cancelUpload() {
    if (!upload.isPending) return;
    abortRef.current?.abort();
    setJob({ kind: "cancelled", fileName: file?.name ?? "" });
  }

  const inFlight = job.kind === "in_flight";

  return (
    <div className="flex flex-col gap-6">
      <div className="inv-card flex flex-col gap-3 p-5" data-pad>
        <h2 className="text-base font-semibold text-ink">Import a source file</h2>
        <p className="max-w-3xl text-sm text-ink-2">
          Choose the provider&apos;s canonical CSV for this campaign. The engine registers the file exactly once by its
          content hash, validates it technically, imports it, and reports how every row was dispositioned.
        </p>
        <p className="max-w-3xl text-sm text-ink-2">
          Uploading here does not attest the source. Attestation is a separate trusted-source operator step, and the
          source list below shows each batch&apos;s attestation state.
        </p>

        <div className="flex flex-col gap-3">
          <label htmlFor="import-file-input" className="inv-label">
            Source file (CSV)
          </label>
          <input
            id="import-file-input"
            type="file"
            accept=".csv,text/csv"
            disabled={inFlight}
            onChange={(event) => pickFile(event.target.files?.[0] ?? null)}
            className="min-h-11 w-full cursor-pointer rounded-card border border-line bg-surface px-3 py-2 text-sm text-ink file:mr-3 file:rounded-card file:border-0 file:bg-surface-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink"
          />
          {file !== null ? (
            <p className="text-sm text-ink-2">
              <span className="font-medium text-ink">{file.name}</span> · Size: {formatBytes(file.size)}
            </p>
          ) : (
            <p className="text-sm text-ink-3">No file chosen yet.</p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="primary"
              className="min-h-11"
              disabled={file === null || inFlight}
              loading={inFlight}
              onClick={startUpload}
            >
              Upload and import
            </Button>
            {inFlight ? (
              <Button type="button" variant="secondary" className="min-h-11" onClick={cancelUpload}>
                Cancel upload
              </Button>
            ) : null}
          </div>
          <p
            role="status"
            tabIndex={-1}
            ref={statusRef}
            data-upload-status="upload-status"
            className="text-sm text-ink-2 focus:outline-none"
          >
            {job.kind === "in_flight"
              ? "In progress. The engine is receiving and importing the file; large files can take a while, so keep this tab open."
              : job.kind === "registered"
                ? "Batch registered and imported. Every row is accounted for below."
                : job.kind === "cancelled"
                  ? "Upload cancelled. This screen cannot confirm the outcome; the source list below shows what actually registered."
                  : ""}
          </p>
        </div>
      </div>

      {/* The job's outcome: exactly one card for the one job this screen runs. */}
      {job.kind === "in_flight" ? (
        <div className="inv-card flex flex-col gap-3 p-5" data-pad data-testid="upload-in-flight">
          <h3 className="text-base font-semibold text-ink">In progress</h3>
          <LoadingState label={`The engine is receiving and importing ${job.fileName} (${job.sizeLabel}). Keep this tab open.`} />
        </div>
      ) : null}

      {job.kind === "registered" ? <RegisteredCard result={job.result} /> : null}

      {job.kind === "cancelled" ? (
        <div className="inv-card flex flex-col gap-2 p-5" data-pad data-testid="upload-cancelled">
          <h3 className="text-base font-semibold text-ink">Upload cancelled</h3>
          <p className="text-sm text-ink-2">
            You cancelled this screen&apos;s upload of {job.fileName}. The engine may still be receiving or importing the
            file it already received; there is no cleanup to confirm with.
          </p>
          <p className="text-sm text-ink-2">The source list below shows what actually registered.</p>
        </div>
      ) : null}

      {job.kind === "failed" ? <FailedCard error={job.error} fileName={job.fileName} onRetry={startUpload} /> : null}

      {/* The campaign's source list: the server's own truth about every registered batch
          and its attestation state. A failed read blocks nothing above - it fails here,
          on its own, with an explicit nothing-inferred sentence (the U01 law). */}
      <div className="inv-card flex flex-col gap-3 p-5" data-pad data-testid="upload-sources">
        <h3 className="text-base font-semibold text-ink">Source batches</h3>
        <p className="text-sm text-ink-2">
          The campaign&apos;s registered source batches, as the server reports them. A batch that is not attested never
          reads as attested here, whatever uploaded it.
        </p>
        {readiness.isError ? (
          <div className="flex flex-col gap-2">
            <ErrorState error={readiness.error} onRetry={() => void readiness.refetch()} />
            <p className="text-sm text-ink-2">
              The source list is not known until this read succeeds. Nothing was inferred, and nothing was changed.
            </p>
          </div>
        ) : readiness.isPending ? (
          <LoadingState label="Loading the source list…" />
        ) : readiness.data !== undefined && readiness.data.sources.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {readiness.data.sources.map((source) => (
              <li key={source.batchId} className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line p-2 text-sm">
                <span className="inv-mono text-xs text-ink-2">{source.batchId}{source.sourceLabel ? ` · ${source.sourceLabel}` : ""}</span>
                {/* The PS7/V03 precedent: unhealthy states carry the neutral chip; the word
                    does the work at AA contrast. */}
                <Chip signal={source.attested && !source.revoked ? "buy" : "ctx"}>
                  {source.revoked ? "Revoked" : source.attested ? "Attested" : "Not attested"}
                </Chip>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-2">No source batches reported for this campaign yet.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// The outcome cards
// ---------------------------------------------------------------------------------------

function RegisteredCard({ result }: { result: IngestBatchResult }) {
  return (
    <div className="inv-card flex flex-col gap-3 p-5" data-pad data-testid="upload-registered">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold text-ink">Batch registered</h3>
        <Chip signal="buy">Registered</Chip>
        <Chip signal="ctx">Not attested</Chip>
      </div>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Batch identity">
        <div><dt className="inv-label">Batch id</dt><dd className="inv-mono mt-1 text-xs text-ink">{result.batchId}</dd></div>
        <div><dt className="inv-label">Content hash (sha256)</dt><dd className="inv-mono mt-1 break-all text-xs text-ink">{result.sha256}</dd></div>
        <div><dt className="inv-label">File</dt><dd className="mt-1 text-sm text-ink">{result.fileName}</dd></div>
        <div><dt className="inv-label">Window</dt><dd className="mt-1 text-sm text-ink">{result.window.start} to {result.window.end} · sequence {result.sequenceNo}</dd></div>
      </dl>
      {result.status === "imported" && result.counters !== null ? (
        <div>
          <h4 className="inv-label">How every row was dispositioned</h4>
          <p className="mb-2 text-xs text-ink-3">
            The engine&apos;s own counts for this batch. Nothing here is attested, selected or delivered by this import;
            those are later, separate steps.
          </p>
          <ul className="flex flex-col gap-1">
            {COUNTER_LABELS.map(({ key, label }) => (
              <li key={key} className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line px-3 py-2 text-sm">
                <span className="text-ink">{label}</span>
                <span className="inv-mono text-xs tabular-nums text-ink-2">
                  {(result.counters?.[key] ?? 0).toLocaleString("en-GB")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-ink-2">
          The engine registered this batch but has not reported an import for it. The source list below shows the batch;
          nothing else is inferred here.
        </p>
      )}
      <p className="text-sm text-ink-2">
        This batch is not attested. Attestation is a separate trusted-source operator step; until it happens the
        campaign&apos;s delivery preview counts this batch&apos;s rows as source not attested.
      </p>
    </div>
  );
}

function FailedCard({ error, fileName, onRetry }: { error: ApiError; fileName: string; onRetry: () => void }) {
  if (error.token === "rejected_duplicate") {
    const existingBatch = typeof error.details?.batch_id === "string" ? error.details.batch_id : null;
    return (
      <div className="inv-card flex flex-col gap-2 p-5" data-pad data-testid="upload-duplicate">
        <h3 className="text-base font-semibold text-ink">This exact file is already registered</h3>
        <p className="text-sm text-ink-2">
          {existingBatch !== null ? <>Existing batch: {existingBatch}. </> : null}
          {typeof error.details?.detail === "string" ? error.details.detail : error.message}
        </p>
        <p className="text-sm text-ink-2">No new import was created, and the registered batch was not changed.</p>
      </div>
    );
  }
  if (error.token === "rejected_schema") {
    const reason = typeof error.details?.reason === "string" ? error.details.reason : null;
    const batchId = typeof error.details?.batch_id === "string" ? error.details.batch_id : null;
    return (
      <div className="inv-card flex flex-col gap-2 p-5" data-pad data-testid="upload-schema-refused">
        <h3 className="text-base font-semibold text-ink">The engine refused the file on technical validation</h3>
        {reason !== null ? <p className="inv-mono text-xs text-ink">{reason}</p> : null}
        {batchId !== null ? <p className="text-xs text-ink-3">Registered for the record as batch {batchId}; the runner never imports it.</p> : null}
        <p className="text-sm text-ink-2">No rows were imported. Check the file against the provider&apos;s canonical columns and choose it again.</p>
        <div>
          <Button type="button" variant="secondary" className="min-h-11" onClick={onRetry}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (error.token === "upload_too_large") {
    return (
      <div className="inv-card flex flex-col gap-2 p-5" data-pad data-testid="upload-too-large">
        <h3 className="text-base font-semibold text-ink">That file is larger than the engine&apos;s 50 MB limit</h3>
        <p className="text-sm text-ink-2">Nothing was uploaded. Split the file or send it through the operator intake path.</p>
      </div>
    );
  }
  if (error.token === "forbidden") {
    return (
      <EmptyState
        tone="error"
        title="You do not have permission to import into this campaign."
        body="The server refused this upload for the selected organisation. Ask your RADAR administrator to review access for the selected organisation. Nothing was uploaded."
      />
    );
  }
  if (error.token === "ingest_import_failed") {
    const batchId = typeof error.details?.batch_id === "string" ? error.details.batch_id : null;
    return (
      <div className="inv-card flex flex-col gap-2 p-5" data-pad data-testid="upload-import-failed">
        <h3 className="text-base font-semibold text-ink">The import failed</h3>
        <p className="text-sm text-ink-2">
          The engine registered {fileName} but the import did not finish
          {batchId !== null ? <> (batch {batchId})</> : ""}. The registration is exactly-once, so retrying the same file
          cannot create a second batch.
        </p>
        <div>
          <Button type="button" variant="secondary" className="min-h-11" onClick={onRetry}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (error.token === "tenant_mismatch") {
    return (
      <EmptyState
        tone="error"
        title="The upload response named a different campaign."
        body="The ingest service answered for a different campaign than the one open here, so nothing is shown. Nothing was changed."
      />
    );
  }
  return (
    <div className="flex flex-col gap-2" data-testid="upload-failed">
      <ErrorState error={error} onRetry={onRetry} />
      <p className="text-sm text-ink-2">The upload of {fileName} did not go through. Nothing is inferred about it here.</p>
    </div>
  );
}
